import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Channel } from 'pusher-js';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import {
  CUBOH_INTEGRATION,
  PUBLISH_BUILD_FAILED,
  PUBLISH_BUILD_SUCCESSFUL,
  PUBLISH_ERROR_MESSAGE,
} from 'constants/menuManagement';

import { getStorefrontDisabled } from 'helpers/cubohStorefront';
import { logException } from 'helpers/loggers';
import { getPublishData } from 'helpers/menuManagement';
import { getStepperBadgeColor } from 'helpers/utils';

import { ActionIcon, Badge, Modal, Stack, Stepper } from '@mantine/core';

import { breakpoints } from 'styles/theme.variables';

import { CloseIconMd } from '@chownow/pantry/icons';
import { ValidationStep } from './ValidationStep';
import { PublishStep } from './PublishStep';
import { ResultsStep } from './ResultsStep';
import type { BadRequestError, Menu, PublishBuildData, PublishResult } from './types';

const PUBLISH_STEPS = {
  validation: 0,
  publish: 1,
  results: 2,
};

const PUBLISH_ALL_STEPS = {
  publish: 0,
  results: 1,
};

const STEP_BADGE_WIDTH = 120;

const IN_PROGRESS = 'in_progress';
const MAX_RETRIES = 1;
// Constant name predates a 60s → 180s bump; value is 3 minutes.
const PUBLISH_BUILD_RETRY_MS = 180_000;

function getPublishBuildErrorMessage(error: unknown): string {
  const response = (error as BadRequestError)?.response;
  if (response?.status === 400) {
    return response.data?.detail ?? PUBLISH_ERROR_MESSAGE;
  }
  return PUBLISH_ERROR_MESSAGE;
}

type PublishMenuModalProps = {
  channels: Channel[] | null;
  close: () => void;
  companies: Record<string | number, unknown>;
  merchants: Record<string | number, unknown>;
  menu: Menu;
  opened: boolean;
  /** Analytics `source`. Omitted by the menu editor, which is the default surface. */
  source?: string;
};

export const PublishMenuModal: FunctionComponent<PublishMenuModalProps> = ({
  opened,
  close,
  channels,
  companies,
  menu,
  merchants,
  source,
}) => {
  const { merchantId, companyId, uuid: menuId } = menu;
  const isPublishAll = menuId === 0;
  const STEPS = isPublishAll ? PUBLISH_ALL_STEPS : PUBLISH_STEPS;

  const [currentStep, setCurrentStep] = useState(0);
  const nextStep = () => setCurrentStep(current => (current < 3 ? current + 1 : current));

  const [publishResult, setPublishResult] = useState<PublishResult | null>(null);

  // Publish-build data is fetched while the user is still on the Validation step
  // (when they click Next), so the modal owns the loading state and passes the
  // resolved data down to PublishStep.
  const [publishData, setPublishData] = useState<PublishBuildData | null>(null);
  const [publishBuildHasError, setPublishBuildHasError] = useState(false);
  const [publishBuildErrorMessage, setPublishBuildErrorMessage] = useState<string | null>(null);
  // Post-publish-click failure (i.e. publishMenus() rejected). Drives the
  // request-level error view on ResultsStep.
  const [publishHasError, setPublishHasError] = useState(false);
  const [publishErrorMessage, setPublishErrorMessage] = useState<string | null>(null);
  const [isFetchingPublishData, setIsFetchingPublishData] = useState(false);
  const retryCountRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // True only while a getPublishData request is in flight, so a duplicate or
  // replayed PUBLISH_BUILD_SUCCESSFUL can't start a second overlapping fetch.
  const fetchInFlightRef = useRef(false);

  const integrations = menu.integrations ?? [];

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);

  // Info to determine if we are trying/can publish to storefront
  const integrationsIncludesStorefront = integrations.some(
    ({ integrationName }) => integrationName === CUBOH_INTEGRATION,
  );
  const storefrontDisabledResult =
    companies[companyId] && merchants[merchantId]
      ? (getStorefrontDisabled({
          selectedCompany: companies[companyId],
          merchant: merchants[merchantId],
          storefrontConfig: undefined,
        }) as {
          stripeConnected: boolean;
          merchantInfoComplete: boolean;
          companyInfoComplete: boolean;
        })
      : undefined;
  const cannotPublishToStorefront =
    !storefrontDisabledResult?.stripeConnected ||
    !storefrontDisabledResult?.merchantInfoComplete ||
    !storefrontDisabledResult?.companyInfoComplete;
  const showStorefrontError = integrationsIncludesStorefront && cannotPublishToStorefront;

  // Dismiss any existing toast notifications so they don't overlap the modal
  useEffect(() => {
    if (opened) {
      notifications.clean();
    }
  }, [opened]);

  const buildChannel =
    companyId &&
    channels?.find(
      (ch: Channel) => ch.name === `${import.meta.env.VITE_WEB_SOCKET_STAGE}-company-${companyId}`,
    );

  // Only advances the user forward when they are coming FROM the Validation step
  // (single-menu flow). In publish-all mode the modal opens directly on the Publish
  // step and the fetch should only populate `publishData` — the user clicks Publish
  // themselves to move on.
  const advanceStep = useCallback(() => {
    setCurrentStep(current => {
      if (isPublishAll) return current;
      if (current !== PUBLISH_STEPS.validation) return current;
      return current + 1;
    });
  }, [isPublishAll]);

  const getAndSetPublishData = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    try {
      setIsFetchingPublishData(true);
      const result = (await getPublishData(menu)) as { data: PublishBuildData } | undefined;
      fetchInFlightRef.current = false;

      if (result?.data?.status === IN_PROGRESS) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          retryCountRef.current += 1;
          if (retryCountRef.current >= MAX_RETRIES) {
            setIsFetchingPublishData(false);
            setPublishBuildHasError(true);
            setPublishBuildErrorMessage(PUBLISH_ERROR_MESSAGE);
            advanceStep();
          } else {
            void getAndSetPublishData();
          }
        }, PUBLISH_BUILD_RETRY_MS);
      } else {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        retryCountRef.current = 0;
        setPublishData(result?.data ?? null);
        setIsFetchingPublishData(false);
        advanceStep();
      }
    } catch (error) {
      fetchInFlightRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      retryCountRef.current = 0;
      setIsFetchingPublishData(false);
      setPublishBuildHasError(true);
      setPublishBuildErrorMessage(getPublishBuildErrorMessage(error));
      advanceStep();
      logException({ message: 'Failed to fetch publish build data', error, context: {}, tags: {} });
    }
  }, [menu, advanceStep]);

  // Triggered by ValidationStep's primary `Next` button. Resets prior fetch state,
  // kicks off a fresh publish-build fetch; the fetch resolution advances to the
  // Publish step (either with data or with an error signal).
  const handleValidationNext = () => {
    setPublishBuildHasError(false);
    setPublishBuildErrorMessage(null);
    setPublishData(null);
    void getAndSetPublishData();
  };

  // Triggered by PublishStep when publishMenus() rejects. Surfaces the
  // request-level error view on ResultsStep.
  const handlePublishFailed = (errorMessage?: string) => {
    setPublishHasError(true);
    setPublishErrorMessage(errorMessage ?? null);
    nextStep();
  };

  // The build channel is company-scoped, so events for unrelated menus can fire
  // here. Only listen while a fetch is in flight, so a stale event can't advance
  // the modal before the user clicks Next.
  useEffect(() => {
    if (!buildChannel || !isFetchingPublishData) return undefined;

    const onBuildSuccessful = () => void getAndSetPublishData();
    const onBuildFailed = (error: unknown) => {
      setIsFetchingPublishData(false);
      setPublishBuildHasError(true);
      setPublishBuildErrorMessage(getPublishBuildErrorMessage(error));
      advanceStep();
    };
    buildChannel.bind(PUBLISH_BUILD_SUCCESSFUL, onBuildSuccessful);
    buildChannel.bind(PUBLISH_BUILD_FAILED, onBuildFailed);

    return () => {
      buildChannel.unbind(PUBLISH_BUILD_SUCCESSFUL, onBuildSuccessful);
      buildChannel.unbind(PUBLISH_BUILD_FAILED, onBuildFailed);
    };
  }, [buildChannel, isFetchingPublishData, getAndSetPublishData, advanceStep]);

  // Reset state and cancel any pending retry when the modal closes.
  useEffect(() => {
    if (!opened) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      retryCountRef.current = 0;
      fetchInFlightRef.current = false;
      setCurrentStep(0);
      setIsFetchingPublishData(false);
      setPublishBuildHasError(false);
      setPublishBuildErrorMessage(null);
      setPublishHasError(false);
      setPublishErrorMessage(null);
      setPublishData(null);
      setPublishResult(null);
    }
  }, [opened]);

  // Publish-all mode skips the Validation step, so there is no Next click to
  // trigger the fetch. Kick it off when the modal opens so PublishStep has data.
  useEffect(() => {
    if (opened && isPublishAll) {
      void getAndSetPublishData();
    }
  }, [opened, isPublishAll, getAndSetPublishData]);

  const GAP = 'md';
  const CONTENT_GAP = 'var(--mantine-spacing-md)';

  const badgeProps = isMobile ? { w: 40, h: 24, px: 'xs2', py: 'xs3' } : { w: STEP_BADGE_WIDTH };

  const handleDoneClick = () => {
    setCurrentStep(0);
    setPublishResult(null);
    close();
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      withCloseButton={false}
      data-testid="publishMenuModal"
      size={isMobile ? 'sm' : 'lg'}
    >
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--dimension-md)' }}
      >
        <ActionIcon onClick={close}>
          <CloseIconMd />
        </ActionIcon>
      </div>
      <Stack w="100%" mt="md">
        <Stepper
          active={currentStep}
          onStepClick={setCurrentStep}
          allowNextStepsSelect={false}
          styles={{
            content: {
              paddingTop: CONTENT_GAP,
            },
          }}
        >
          {!isPublishAll && (
            <Stepper.Step
              withIcon={false}
              label={
                <Badge
                  size="sm"
                  variant={getStepperBadgeColor({
                    currentStep,
                    stepIndex: PUBLISH_STEPS.validation,
                  })}
                  {...badgeProps}
                >
                  1{!isMobile ? '. validation' : ''}
                </Badge>
              }
            >
              <ValidationStep
                gap={GAP}
                channels={channels}
                nextStep={handleValidationNext}
                isFetchingNextStepData={isFetchingPublishData}
                handleDoneClick={handleDoneClick}
                menu={{ ...menu, uuid: String(menu.uuid) }}
                showStorefrontError={showStorefrontError}
                integrationsIncludesStorefront={integrationsIncludesStorefront}
              />
            </Stepper.Step>
          )}
          <Stepper.Step
            withIcon={false}
            label={
              <Badge
                size="sm"
                variant={getStepperBadgeColor({ currentStep, stepIndex: STEPS.publish })}
                {...badgeProps}
              >
                {STEPS.publish + 1}
                {!isMobile ? '. publish' : ''}
              </Badge>
            }
          >
            <PublishStep
              gap={GAP}
              menu={menu}
              nextStep={nextStep}
              handleDoneClick={handleDoneClick}
              handlePublishFailed={handlePublishFailed}
              setPublishResult={setPublishResult}
              publishData={publishData}
              publishBuildHasError={publishBuildHasError}
              publishBuildErrorMessage={publishBuildErrorMessage}
              isFetchingPublishData={isFetchingPublishData}
              source={source}
            />
          </Stepper.Step>
          <Stepper.Step
            withIcon={false}
            label={
              <Badge
                size="sm"
                variant={getStepperBadgeColor({ currentStep, stepIndex: STEPS.results })}
                {...badgeProps}
              >
                {STEPS.results + 1}
                {!isMobile ? '. results' : ''}
              </Badge>
            }
          >
            <ResultsStep
              gap={GAP}
              publishResult={publishResult}
              publishHasError={publishHasError}
              publishErrorMessage={publishErrorMessage}
              handleDoneClick={handleDoneClick}
            />
          </Stepper.Step>
        </Stepper>
      </Stack>
    </Modal>
  );
};
